import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  clearAnchorAll,
  updateAnchorListItem,
} from "../../../../entities/anchorListitem/anchorListItem.actions"
import { usePrevious } from "../../../../hooks"
import { isDomAvailable } from "../../../../lib"
import { getAttributesFromUrl } from "../../../../utils"
import { ProductsView } from "./Products.view"
import { useDynamicsProductsLoader } from "./useDynamiqueProductLoader"

interface Props {
  className?: string
  pageContext: {
    data: any
    categoryId: string
    categoryName: string
    type: string
    filters: any[]
    producTypeId: any
    description: string
    contenuSEO: string
    liens_haut_page: string,
    liens_bas_page: string,
    titre_lien_bas_page: string,
    faq: { question: string, reponse: string },
    seoTitle: string
    seoDescription: string
  }
}


export function ProductsContainer({ pageContext, data }: Props) {

  const filterIsChanging = useRef(false)
  const [attributes, setAttributes] = useState(getAttributesFromUrl())

  // bug sur usePrevious 
  const prevAttributes = usePrevious(attributes)

  const {
    itemAnchor,
    after,
    products: reduxProducts,
    url,
  } = useSelector(state => state.anchorListItemReducer)


  const removeAfterForBack = () => {
    setProducts([])
    dispatch(clearAnchorAll())
  }


  const isBackFn = () => {
    const slug = data?.saleor?.category?.slug || ""

    return url.includes(slug)
  }


  useLayoutEffect(() => {
    const isBack = isBackFn()

    if (!isBack) removeAfterForBack()

  }, [window.location, pageContext, data])

  const dispatch = useDispatch()


  const getPreviousProducts = () => {
    if (!isBackFn()) return []

    return reduxProducts
  }


  const pageInfo = useRef(pageContext?.data?.pageInfo || {})

  const [dynamicGetProducts, getProductsFiltred] = useDynamicsProductsLoader(pageContext)


  const productsInit = useMemo(
    () => {
      return getPreviousProducts()
    }
    ,
    [reduxProducts, url, isBackFn()]
  )

  const [products, setProducts] = useState(productsInit || new Map())


  useEffect(() => {

    if (dynamicGetProducts.called && !dynamicGetProducts.loading) {
      onNewProducts(dynamicGetProducts)
    }

  }, [dynamicGetProducts])


  const checkIfFilterAreChanging = (
    prevAttributes: any[],
    attributes: any[]
  ): boolean => {
    if (!prevAttributes) {
      prevAttributes = []
    }

    const hasher = (previousValue, currentValue) => {
      previousValue += currentValue.slug

      if (Array.isArray(currentValue?.values)) {
        previousValue += "|" + currentValue?.values?.join("|")
      }

      return previousValue
    }

    const prevHash = prevAttributes.reduce(hasher, "")
    const currentHash = attributes.reduce(hasher, "")

    return prevHash != currentHash
  }


  const onLoadMore = async () => {

    filterIsChanging.current = false

    getProductsFiltred()
  }

  const onNewProducts = dynamicGetProducts => {
    const newProducts = dynamicGetProducts?.data?.products?.edges.map(({ node }) => [node.id, node])

    const result = [...products, ...new Map(newProducts)]

    setProducts(result)
    pageInfo.current = dynamicGetProducts.data?.products?.pageInfo
    saveAfterForBack(dynamicGetProducts.data?.products?.pageInfo?.endCursor, result, result.length)

  }

  const onFiltersChange = data => {
    let res = getAttributesFromUrl()
    setAttributes(res)
    const attributechanged = checkIfFilterAreChanging(
      prevAttributes || attributes,
      res
    )
    if (attributechanged) {
      filterIsChanging.current = true
      removeAfterForBack()
      getProductsFiltred()
    } else {
      filterIsChanging.current = false
    }
  }

  // refresh la page en enlevant les param de l'url
  const onClearFilter = () => {
    if (isDomAvailable()) {
      const urlWithoutParam = window.location.href.split("?")[0]
      window.location.href = urlWithoutParam
    }
    removeAfterForBack()
  }

  const saveAfterForBack = (
    after: string,
    items: [],
    numberOfItems: number | null
  ) => {
    if (after) {
      dispatch(
        updateAnchorListItem(
          after,
          numberOfItems || 0,
          items,
          window.location.href
        )
      )
    }
  }


  return (
    <ProductsView
      products={products}
      filters={pageContext.filters}
      onFiltersChange={onFiltersChange}
      onClearFilter={onClearFilter}
      onLoadMore={onLoadMore}
      loading={dynamicGetProducts.loading}
      error={dynamicGetProducts.error}
      totalCount={dynamicGetProducts?.data?.products?.totalCount || 0}
      categoryName={pageContext.categoryName}
      description={pageContext.description}
      contenuSEO={pageContext.contenuSEO}
      liens_haut_page={pageContext.liens_haut_page}
      liens_bas_page={pageContext.liens_bas_page}
      faq={pageContext.faq}
      seoTitle={pageContext.seoTitle}
      seoDescription={pageContext.seoDescription}
      titre_lien_bas_page={pageContext.titre_lien_bas_page}
      // on passe soit la collection soit l'id pour requete plus tard (breadcrumbs )
      category={data?.saleor?.category || { id: data.allStrapiCollectionContent?.edges[0]?.node?.categorie_mere?.saleor_id }}

    />
  )
}



